import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { sanitizeTourMarkup } from "../utils/sanitizeTourMarkup";
import "../styles/app-tour.css";

const MOBILE_BREAKPOINT = 640;

const TOUR_TARGET_FALLBACKS = {
  profile: ".homeAvatar",
  "family-switcher": ".aileSwitcherTopBtn",
  notifications: ".homeBildirimBtn",
  settings: ".homeSettings",
  hero: ".heroSlider",
  "quick-stats": ".homeMiniStats",
  "family-status": ".familyStatusCard",
  modules: ".modernMenuGrid",
};

const findTourTarget = (targetId) => {
  if (!targetId) return null;
  return (
    document.querySelector(`[data-tour-id="${targetId}"]`) ||
    document.querySelector(TOUR_TARGET_FALLBACKS[targetId] || "")
  );
};

export default function AppTour({ active, onComplete, onSkip }) {
  const { t } = useLanguage();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [targetMarkup, setTargetMarkup] = useState("");
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });

  const steps = useMemo(() => [
    { id: "gestures", target: null, title: t("tour.gestures.title"), text: t("tour.gestures.intro"), gestures: true },
    { id: "profile", target: "profile", title: t("tour.profile.title"), text: t("tour.profile.text"), cloneTarget: true },
    { id: "family", target: "family-switcher", title: t("tour.family.title"), text: t("tour.family.text") },
    { id: "notifications", target: "notifications", title: t("tour.notifications.title"), text: t("tour.notifications.text") },
    { id: "settings", target: "settings", title: t("tour.settings.title"), text: t("tour.settings.text") },
    { id: "summary", target: "hero", title: t("tour.summary.title"), text: t("tour.summary.text") },
    { id: "quick-stats", target: "quick-stats", title: t("tour.quickStats.title"), text: t("tour.quickStats.text") },
    { id: "family-status", target: "family-status", title: t("tour.familyStatus.title"), text: t("tour.familyStatus.text") },
    { id: "modules", target: "modules", title: t("tour.modules.title"), text: t("tour.modules.text") },
    { id: "done", target: null, title: t("tour.done.title"), text: t("tour.done.text"), final: true },
  ], [t]);

  const step = steps[index] || steps[0];

  useEffect(() => {
    if (!active) return;
    setIndex(0);
    const previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => { document.documentElement.style.overflow = previousOverflow; };
  }, [active]);

  useLayoutEffect(() => {
    if (!active) return undefined;
    let frame = 0;
    let timer = 0;

    const measure = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        setViewport({ width: window.innerWidth, height: window.innerHeight });
        if (!step.target) { setRect(null); setTargetMarkup(""); return; }
        const target = findTourTarget(step.target);
        if (!target) { setRect(null); setTargetMarkup(""); return; }
        setTargetMarkup(step.cloneTarget ? sanitizeTourMarkup(target) : "");
        const r = target.getBoundingClientRect();
        const pad = step.target === "modules" ? 8 : 7;
        setRect({
          top: Math.max(8, r.top - pad),
          left: Math.max(8, r.left - pad),
          width: Math.min(window.innerWidth - 16, r.width + pad * 2),
          height: Math.min(window.innerHeight - 16, r.height + pad * 2),
        });
      });
    };

    if (step.target) {
      const target = findTourTarget(step.target);
      if (target) {
        const r = target.getBoundingClientRect();
        const safeTop = 88;
        const safeBottom = window.innerHeight - 210;
        if (r.top < safeTop || r.bottom > safeBottom) {
          target.scrollIntoView({ behavior: "smooth", block: step.target === "modules" ? "start" : "center", inline: "nearest" });
          timer = window.setTimeout(measure, 420);
        }
      }
    }
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [active, step]);

  if (!active) return null;

  const mobile = viewport.width <= MOBILE_BREAKPOINT;
  let tooltipStyle = {};
  if (!mobile && rect) {
    const tooltipWidth = Math.min(380, viewport.width - 32);
    const below = viewport.height - (rect.top + rect.height);
    const useBelow = below >= 230 || rect.top < 245;
    tooltipStyle = {
      width: tooltipWidth,
      left: Math.min(Math.max(16, rect.left + rect.width / 2 - tooltipWidth / 2), viewport.width - tooltipWidth - 16),
      top: useBelow ? Math.min(viewport.height - 214, rect.top + rect.height + 16) : undefined,
      bottom: useBelow ? undefined : Math.max(16, viewport.height - rect.top + 16),
    };
  }

  const next = () => {
    if (step.final || index >= steps.length - 1) onComplete?.();
    else setIndex((v) => v + 1);
  };

  const previous = () => setIndex((v) => Math.max(0, v - 1));

  const content = (
    <div className={`appTourRoot${rect && !step.final ? " appTourHasTarget" : " appTourNoTarget"}`} role="dialog" aria-modal="true" aria-label={t("tour.ariaLabel")}>
      {rect && !step.final && (
        <>
          <div className="appTourShade appTourShadeTop" style={{ height: rect.top }} aria-hidden="true" />
          <div className="appTourShade appTourShadeLeft" style={{ top: rect.top, width: rect.left, height: rect.height }} aria-hidden="true" />
          <div className="appTourShade appTourShadeRight" style={{ top: rect.top, left: rect.left + rect.width, right: 0, height: rect.height }} aria-hidden="true" />
          <div className="appTourShade appTourShadeBottom" style={{ top: rect.top + rect.height, bottom: 0 }} aria-hidden="true" />
          <div className="appTourSpotlight" style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }} aria-hidden="true" />
          {step.cloneTarget && targetMarkup && (
            <div
              className="appTourTargetClone"
              style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
              aria-hidden="true"
              dangerouslySetInnerHTML={{ __html: targetMarkup }}
            />
          )}
        </>
      )}
      <section className={`appTourCard${mobile || !rect ? " appTourCardBottom" : ""}`} style={mobile || !rect ? undefined : tooltipStyle}>
        <div className="appTourProgress" aria-label={`${index + 1}/${steps.length}`}>
          {steps.map((s, i) => <span key={s.id} className={i === index ? "active" : i < index ? "done" : ""} />)}
        </div>
        <div className="appTourStepLabel">{t("tour.step", { current: index + 1, total: steps.length })}</div>
        <h2>{step.title}</h2>
        <p>{step.text}</p>
        {step.gestures && (
          <div className="appTourGesturePanel" aria-label={t("tour.gestures.ariaLabel")}>
            <div className="appTourGestureItem">
              <div className="appTourGestureVisual appTourGestureBackVisual" aria-hidden="true">
                <span className="appTourGestureEdge appTourGestureEdgeLeft" />
                <span className="appTourGestureFinger appTourGestureFingerBack">●</span>
                <span className="appTourGestureArrow">→</span>
              </div>
              <div>
                <strong>{t("tour.gestures.back.title")}</strong>
                <span>{t("tour.gestures.back.text")}</span>
              </div>
            </div>
            <div className="appTourGestureItem">
              <div className="appTourGestureVisual appTourGestureMenuVisual" aria-hidden="true">
                <span className="appTourGestureEdge appTourGestureEdgeRight" />
                <span className="appTourGestureFinger appTourGestureFingerMenu">●</span>
                <span className="appTourGestureArrow">←</span>
              </div>
              <div>
                <strong>{t("tour.gestures.menu.title")}</strong>
                <span>{t("tour.gestures.menu.text")}</span>
              </div>
            </div>
          </div>
        )}
        <div className="appTourActions">
          <button type="button" className="appTourSkip" onClick={onSkip}>{t("tour.skip")}</button>
          <div className="appTourNavActions">
            {index > 0 && <button type="button" className="appTourBack" onClick={previous}>{t("tour.back")}</button>}
            <button type="button" className="appTourNext" onClick={next}>{step.final ? t("tour.finish") : t("tour.next")}</button>
          </div>
        </div>
      </section>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}
